import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { showDates, venueLine } from "../../core/primitives/hosted-event";
import {
  VendorShowsSectionController,
  type VendorShowsSectionDeps,
} from "../events/sections/vendor-shows/vendor-shows-section-controller";
import { OnboardingShowActions, OnboardingShowState } from "./onboarding-show-model";

export type OnboardingShowDeps = VendorShowsSectionDeps & Pick<AppDeps, "locationsRepository">;

export interface OnboardingShowRouting {
  toSelling(storeId: string): void;
  toPayments(storeId: string): void;
}

/**
 * Step 3 for a vendor: the table. Redeemed by the code from the email, or bought
 * here from the shows on sale; either way the table brings a booth, which is
 * all the location a vendor needs. A table bought with the account's verified
 * email is the shop's already, from the moment the shop was created.
 */
export class OnboardingShowController extends SubtreeController implements OnboardingShowActions {
  readonly state = new OnboardingShowState();
  readonly shows: VendorShowsSectionController;

  constructor(
    readonly deps: OnboardingShowDeps,
    readonly routing: OnboardingShowRouting,
  ) {
    super();
    this.subtree.put(OnboardingShowState, this.state);
    this.subtree.put(OnboardingShowActions, this);
    this.shows = this.own(new VendorShowsSectionController(this.subtree, deps));
    this.sync(() => {
      this.state.storeId.value = deps.storeRepository.shopId;
    }, [deps.storeRepository]);
    this.sync(() => {
      const show = this.shows.state.shows.value.find((s) => s.myTables.length > 0);
      const table = show?.myTables[0];
      this.state.table.value =
        show && table
          ? { label: table.label, show: show.title, when: showDates(show), where: venueLine(show.place) }
          : null;
      this.state.hasBooth.value =
        this.state.table.value !== null || deps.locationsRepository.locations.some((l) => l.kind === "BOOTH");
    }, [deps.locationsRepository, this.shows.state.shows]);
  }

  startBooking = () => {
    this.state.booking.value = true;
  };

  back = () => {
    this.routing.toSelling(this.state.storeId.value ?? "");
  };

  next = () => {
    if (!this.state.hasBooth.value) return;
    this.routing.toPayments(this.state.storeId.value ?? "");
  };
}
